import type { DisplayRect } from 'features/knowledgeDocumentViewer/components/viewer/shared/liveValueDisplayRect'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  ActionMode,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useBoxLinkedDocuments } from 'features/knowledgeDocumentViewer/hooks/useBoxLinkedDocuments'
import { useOffPageConnectorDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffPageConnectorDocuments'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import React, { useCallback, useEffect, useState } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

import { useBoxHighlight } from './useBoxHighlight'
import { useBoxStyles } from './useBoxStyles'

type UseViewerOverlayStateArgs = {
  adjustedBoxes: Box[]
  highlightBoxIds: string[]
  actionMode: ActionMode
  showTooltips: boolean
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>
  viewerContainerRef: React.RefObject<HTMLDivElement | null>
  onBoxHover?: (box: Box | null) => void
  /** Where each box's live value chip ended up, when it was displaced. */
  chipRectByBoxId: Map<string, DisplayRect>
}

export function useViewerOverlayState({
  adjustedBoxes,
  highlightBoxIds,
  actionMode,
  showTooltips,
  transformRef,
  viewerContainerRef,
  onBoxHover,
  chipRectByBoxId,
}: UseViewerOverlayStateArgs) {
  const [tooltipBox, setTooltipBox] = useState<Box | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const [internalHighlightedBoxIds, setInternalHighlightedBoxIds] =
    useBoxHighlight(highlightBoxIds)

  const { siteID } = useKnowledgeDocumentDataContext()
  const {
    knowledgeDocumentEntriesForSelectedDocument,
    hasTimeSeriesData,
    getMatchedDatapoints,
  } = useDocumentTabDataContext()
  const {
    hoverId,
    handleMouseEnter: contextHandleMouseEnter,
    handleMouseLeave: contextHandleMouseLeave,
  } = useKnowledgeDocumentUXContext()

  const { strokeFor, fillFor } = useBoxStyles(
    hoverId,
    internalHighlightedBoxIds,
    actionMode
  )

  const offPageConnectorSearchText =
    tooltipBox?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR
      ? tooltipBox.label
      : undefined

  const {
    systemDocumentEntries: offPageConnectorDocuments,
    isLoading: isLoadingOffPageConnector,
  } = useOffPageConnectorDocuments(offPageConnectorSearchText, siteID)

  const { connectedKnowledgeDocumentEntries } = useBoxLinkedDocuments(
    knowledgeDocumentEntriesForSelectedDocument,
    tooltipBox,
    siteID
  )

  const tooltipLinkedCount = connectedKnowledgeDocumentEntries?.length ?? 0

  const handleMouseEnter = useCallback(
    (box: Box) => {
      contextHandleMouseEnter(box)
      onBoxHover?.(box)
    },
    [contextHandleMouseEnter, onBoxHover]
  )

  const handleMouseLeave = useCallback(() => {
    contextHandleMouseLeave()
    onBoxHover?.(null)
  }, [contextHandleMouseLeave, onBoxHover])

  const handleBoxMouseEnter = useCallback(
    (box: Box, evt: React.MouseEvent<SVGElement>) => {
      handleMouseEnter(box)

      if (!showTooltips || !viewerContainerRef.current) {
        return
      }

      const containerRect = viewerContainerRef.current.getBoundingClientRect()
      setTooltipBox(box)
      setTooltipPosition({
        x: evt.clientX - containerRect.left,
        y: evt.clientY - containerRect.top,
      })
    },
    [handleMouseEnter, showTooltips, viewerContainerRef]
  )

  const handleBoxMouseLeave = useCallback(() => {
    handleMouseLeave()
    setTooltipBox(null)
    setTooltipPosition(null)
  }, [handleMouseLeave])

  const updateTooltipPosition = useCallback(() => {
    if (!showTooltips || !hoverId) {
      if (!hoverId && tooltipBox) {
        setTooltipBox(null)
        setTooltipPosition(null)
      }
      return
    }

    const matchingBox = adjustedBoxes.find((box) => box.id === hoverId)
    if (!matchingBox || !viewerContainerRef.current || !transformRef.current) {
      return
    }

    const transform = transformRef.current.instance.transformState
    const containerRect = viewerContainerRef.current.getBoundingClientRect()
    // A displaced chip, not the box it came from, is what the reader is
    // pointing at, so the tooltip follows the chip wherever it was placed.
    const chipRect = chipRectByBoxId.get(matchingBox.id)
    const anchorCenterX = chipRect
      ? chipRect.x + chipRect.width / 2
      : matchingBox.x + matchingBox.w / 2
    const anchorTopY = chipRect ? chipRect.y : matchingBox.y
    const screenX = anchorCenterX * transform.scale + transform.positionX
    const screenY = anchorTopY * transform.scale + transform.positionY

    if (
      screenX >= 0 &&
      screenX <= containerRect.width &&
      screenY >= 0 &&
      screenY <= containerRect.height
    ) {
      setTooltipBox(matchingBox)
      setTooltipPosition({ x: screenX, y: screenY })
    }
  }, [
    adjustedBoxes,
    chipRectByBoxId,
    hoverId,
    showTooltips,
    tooltipBox,
    transformRef,
    viewerContainerRef,
  ])

  useEffect(() => {
    updateTooltipPosition()
  }, [updateTooltipPosition])

  return {
    fillFor,
    getMatchedDatapoints,
    handleBoxMouseEnter,
    handleBoxMouseLeave,
    hasTimeSeriesData,
    isLoadingOffPageConnector,
    offPageConnectorDocuments,
    setInternalHighlightedBoxIds,
    strokeFor,
    tooltipBox,
    tooltipLinkedCount,
    tooltipPosition,
    updateTooltipPosition,
  }
}
